import { useField } from 'formik';
import axiosClient from 'config/axios';
import { FileExcelOutlined, UploadOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import Button from 'components/new-ui/base/button/Button';
import BulkUpload, { BulkUploadUploadFunc, FileType } from 'components/ui/composed/bulkUpload/BulkUpload';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { downloadIrelandH1ImportDeclarationSubmissionDetails } from 'store/declarations/client';
import { PresignedUrlResponse } from 'store/documents/document';
import { uploadFile } from 'utils/uploadFile';
import { BrokerUploadSteps } from '../types';
import { RcFile } from 'antd/lib/upload';

const stepKey = BrokerUploadSteps.BULK_UPLOAD;

const BulkUploadStep = () => {
    const [, , s3LinkHelpers] = useField('bulkUpload.s3Link');
    const [fileProps, , fileHelpers] = useField('bulkUpload.file');
    const { isActive, isComplete, isIdle, resetStep } = useMultiStepFormContext<any>();

    const value = fileProps.value as RcFile | undefined;

    const sectionTitle = 'Bulk Submission (Optional)';

    const handleUpload: BulkUploadUploadFunc = async (options) => {
        const file = options.file as File;
        const preSignUrlPayload = { fileSize: file.size, fileName: file.name };

        try {
            const { preSignedUrl }: PresignedUrlResponse = await axiosClient
                .get('/cms/shared/files/upload', { params: preSignUrlPayload })
                .then((res) => res.data.payload);

            await uploadFile(file, preSignedUrl);

            const match = preSignedUrl.match(/\/([^/?]+)\?/);
            const uuid = match?.[1];

            const result = await axiosClient.post('/cms/shared/files', { s3key: uuid, ...preSignUrlPayload });
            const newLink = result.data.payload.link;

            if (newLink) {
                s3LinkHelpers.setValue(newLink);
                fileHelpers.setValue(file);
                return true;
            }
        } catch (error) {
            notification.error({ message: 'An error occurred while uploading the file.' });
        }

        return false;
    };

    const handleDelete = () => {
        s3LinkHelpers.setValue(null);
        fileHelpers.setValue(null);
    };

    const handleDownload = () => downloadIrelandH1ImportDeclarationSubmissionDetails();

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<FileExcelOutlined />} />;
    }

    if (isComplete(stepKey)) {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <FormSectionHeader title={sectionTitle} icon={<FileExcelOutlined />} />
                <ReadonlyFormItem
                    disabled={false}
                    value={value?.name}
                    onReset={() => {
                        resetStep(stepKey, 's3Link');
                        handleDelete();
                    }}
                />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader
                    title={sectionTitle}
                    icon={<FileExcelOutlined />}
                    button={
                        <Button
                            icon={<UploadOutlined />}
                            onClick={() => document.getElementById('bulk-submission-upload')?.click()}
                        >
                            Upload
                        </Button>
                    }
                />
                <BulkUpload
                    id="bulk-submission-upload"
                    showTitle={false}
                    showButton={false}
                    showDownloadLink={false}
                    onDownload={handleDownload}
                    onUpload={handleUpload}
                    onDelete={handleDelete}
                    fileType={[FileType.XLS, FileType.XLSX, FileType.CSV]}
                />
            </div>
        );
    }

    return null;
};

export default BulkUploadStep;
